// ULL — inngangspunktet. Binder kilde → lager → skjermer.
//
// ARKITEKTUREN I ÉN SETNING: én uforanderlig tilstand i `lib/lager.ts`, én kilde
// som leverer UGS-meldinger, og skjermer som tegner seg helt om når noe endres.
// Samme mønster som NJORD, av samme grunn — men med tre ting NJORD ikke har, og
// som alle følger av at ULL er et jegerlag og ikke en fast post.
//
// ══ 1. EMCON: APPEN STRÅLER, OG DET SKAL SYNES ═════════════════════════════
// En app som sender kontinuerlig ER en sender, og et jegerlag bak fiendens linjer
// kan bli peilet inn på den. Derfor:
//   * Sending er AV som standard. Ikke «av til noe er konfigurert», men av som
//     normaltilstand.
//   * ALT utgående går i en KØ. Det finnes ingen vei rundt: `ctx.send()` legger i
//     køen og sender bare hvis bryteren står på. Kommandoene forsvinner ikke, og
//     de går ikke ut i smug.
//   * Køen tømmes bare av et bevisst trykk. Det finnes INGEN periodisk
//     opplasting i denne appen — sekundtimeren under tegner skjermen, den sender
//     ingenting.
//   * Tilstanden vises med ORD, ikon og en stripe over hele flata når sending er
//     på. Aldri farge alene: flata brukes i mørket, ofte med NVG.
//
// Den LOKALE lenken til broen (en boks i lagets eget skjulsted) er ikke bak
// bryteren — se toppkommentaren i `api/kilde.ts` om hvorfor det er to ulike lenker.
//
// ══ 2. FRISKHET MÅLES PÅ HJERTEBANK, IKKE PÅ UTLØSNINGER ═══════════════════
// En geofon som ikke har utløst på en time er ikke stille — det har bare ikke gått
// noen der. Se `sensorStatuser` i `lib/lager.ts`. Sensorstolpen viser derfor TO
// aldre per node, og de betyr helt ulike ting.
//
// ══ 3. HOVEDSKJERMEN ER SEKVENSANALYSEN ════════════════════════════════════
// Ikke en liste over utløsninger. Se toppkommentaren i `skjermer/sekvensanalyse.ts`.
//
// FULL OMTEGNING HVERT SEKUND, som i NJORD: alt her er tidsavhengig (nodenes
// livstegn, utløsningenes alder, hvilke sekvenser som fortsatt pågår). En finkornet
// modell måtte likevel tegnet om nesten alt, men med risiko for at ÉN rad viser
// gammel alder ved siden av ny — og på en flate der hele poenget er å stole på
// tidene, er det den verste feilen. ÉN KLOKKE PER TEGNING (`ctx.naa()`).
//
// ══ STILARKENE MÅ IMPORTERES HERFRA ════════════════════════════════════════
// Vite tar bare med CSS som er importert fra JS, og det finnes ingen `<link>` i
// index.html. Mangler importene, blir bygget levert som USTILT HTML — og både
// bygget og `tokens.test.ts` går grønt likevel, for den leser CSS-filene fra disk
// og svarer på «er tokenene riktige», ikke på «får nettleseren dem». Denne feilen
// er gjort før i dette programmet, og `stilark.test.ts` finnes for at et menneske
// som åpner siden ikke skal være oppdagelsesmetoden neste gang.
//
// theme.css FØRST: den definerer tokenene style.css bruker.
import './theme.css';
import './style.css';

import {
  FRISKHET_NAVN,
  NIVAA_NAVN,
  div,
  formaterAlder,
  formaterKlokke,
  knapp,
  lagKunngjører,
  lagOvelseskilde,
  lagPanel,
  lesOvelsevalg,
  manglerKilde,
  merke,
  montérPoppetPanel,
  nivaaKlasse,
  ovelseAvgjort,
  ovelsebanner,
  postTillit,
  sett,
  settFlatenavn,
  settOvelsesramme,
  skrivOvelsevalg,
  span,
  statusprikk,
  utgaaendeNektet,
  type PostTillit,
} from '@skade/sensorflate';
import type { UgsArt, UgsMalklasse } from '@skade/contracts';
import { ULL_MEDIA, ULL_WS, lagKilde, type Kilde, type Tilkobling } from './api/kilde.js';
import { ovelsemeldinger, ovelsepuls } from './api/ovelse.js';
import {
  SENSORART_NAVN,
  ULLFLAGG_NAVN,
  batteriAdvarsel,
  batteriTekst,
  type Rute,
  type UllKommando,
  type UllMelding,
  type Ullflagg,
} from './api/typer.js';
import { nodeoppsummering } from './lib/oppsummering.js';
import {
  bruk, brukRute, sensorStatuser, settFlagg, sisteUtlosningFor, tomtLager,
  type Lagertilstand,
} from './lib/lager.js';
import { tegnSekvensanalyse } from './skjermer/sekvensanalyse.js';
import { tegnDekning } from './skjermer/dekning.js';
import { tegnBilde } from './skjermer/bilde.js';
import { tegnMelding } from './skjermer/melding.js';
import type { Bildevurdering, Kontekst, Okt, Sendetilstand } from './skjermer/kontekst.js';
import { ART_IKON, ikon } from './ui/ikoner.js';

const OVELSE_NØKKEL = 'ull.ovelse';

/**
 * Rolle fra URL er et UTVIKLINGSHJELPEMIDDEL, aldri en innlogging.
 *
 * ULL har ingen autentiseringstjeneste, og skal kanskje aldri ha en — det finnes
 * ikke nødvendigvis nett der laget står. Alternativet ville vært å la alle knapper
 * være aktive, altså å late som jegeren har alle rettigheter, og det er verre enn å
 * ikke ha rettigheter i det hele tatt: det oppdages først når hubben avviser
 * handlingen. `opphav` bæres med og vises i toppstripa.
 */
function lesØkt(søk = location.search): Okt {
  const rolle = new URLSearchParams(søk).get('rolle');
  if (!rolle) return { bruker: null, opphav: 'ingen' };
  return {
    bruker: { id: `dev:${rolle}`, role: rolle as never, org: {} } as never,
    opphav: 'url-parameter',
  };
}

function start(): void {
  const rot = document.getElementById('app');
  if (!rot) throw new Error('ULL: #app finnes ikke i index.html');

  // Panelmalen er delt med NJORD. Uten eget flatenavn ville to flater som popper
  // ut «dekning» overtatt hverandres nettleservindu.
  settFlatenavn('ull');

  let tilstand: Lagertilstand = tomtLager();
  let ruter: Rute[] = [];
  let valgtId: string | null = null;
  let valgtMelding: string | null = null;
  const lyttere = new Set<() => void>();
  const kunngjører = lagKunngjører();

  // ── ØVINGSDATA ────────────────────────────────────────────────────────────
  // Porten avgjør, ikke ønsket: er `VITE_ULL_WS` satt, er øving NEKTET uansett hva
  // som står i URL-en eller i nettleserlagringen — også når broen er nede. Se krav
  // 3 i `@skade/sensorflate/ovelse.ts`. Håndhevet i koden: `lagOvelseskilde`
  // KASTER om den konstrueres med en broadresse.
  const ovelsevedtak = ovelseAvgjort({
    broAdresse: ULL_WS,
    søk: location.search,
    lagret: lesOvelsevalg(OVELSE_NØKKEL),
  });
  const ovelse = ovelsevedtak.på;

  // ── EMCON ────────────────────────────────────────────────────────────────
  // Køen er en enkel liste, ikke en `Outbox` med automatisk gjenforsøk: en jeger
  // skal se presis hva som ligger der og bestemme selv når det går ut.
  // Automatisk retry ville vært å stråle uten et trykk.
  const kø: UllKommando[] = [];
  let sendingPa = false;
  let sistSendtMs: number | null = null;
  let sender = false;

  // Jegerens egne bildevurderinger. De ligger UTENFOR lageret fordi lageret er
  // BROENS bilde: en vurdering er et menneskes observasjon, og den skal ikke
  // kunne overskrives av neste melding fra broen.
  const bildevurderinger = new Map<string, Bildevurdering>();

  // Klokka settes ÉN gang per tegning, i `tegn()`.
  let naaMs = Date.now();

  /**
   * Kilden. Øvingskilden er den DELTE (`lagOvelseskilde`) — bare manuset er ULLs.
   * Den later ALDRI som den er tilkoblet: `tilkobling()` melder `ovelse`, og
   * toppstripa, banneret og ramma sier det høyt.
   *
   * MERK forskjellen fra den forrige demokilden: den sendte alt ÉN gang, og da
   * kunne man ikke øve på skillet mellom LIVSTEGN og SISTE UTLØSNING — begge
   * aldrene bare vokste. Pulsen sender livstegn ofte og utløsninger sjelden, som
   * er hele grunnen til at sensorstolpen har to alderskolonner. Se `ovelsepuls`.
   */
  const kilde: Kilde = ovelse
    ? lagOvelseskilde<UllMelding>({
        broAdresse: ULL_WS,
        start: ovelsemeldinger,
        puls: ovelsepuls,
      })
    : lagKilde(ULL_WS);

  /** Slå øvingen av eller på, og last flata på nytt med et rent lager. */
  function settOvelse(på: boolean): void {
    skrivOvelsevalg(OVELSE_NØKKEL, på ? '1' : '0');
    // Omlasting og ikke en tilstandsbryter: å bytte kilde under drift ville
    // etterlatt øvingsutløsninger i lageret ved siden av ekte, og da er merkingen
    // det eneste som skiller dem i en sekvensanalyse. Et rent lager er billigere
    // å stole på — og køen skal ikke kunne overleve overgangen.
    location.search = på ? '?ovelse=1' : '?ovelse=0';
  }

  function sendetilstand(): Sendetilstand {
    return { pa: sendingPa, iKo: kø.length, sistSendtMs, sender, ovelse };
  }

  /**
   * Legg en kommando i køen, og send den NÅ hvis sending er på.
   *
   * Returnerer `false` når ingenting gikk ut — og kallstedet SKAL si det. En
   * `false` betyr ikke at handlingen er tapt: den ligger i køen.
   *
   * ══ ØVINGSDATA KOMMER IKKE INN I KØEN I DET HELE TATT (krav 4) ════════════
   * Den forrige demokilden lot køen FYLLES og bare `send()` returnere `false`, med
   * begrunnelsen at «da kan EMCON-oppførselen prøves ut». Det var galt, og på den
   * farligste måten: køen overlever både at demoen slås av og at flata lastes på
   * nytt i samme økt, og en kø med oppdiktede meldinger er en ladd geværpipe som
   * peker mot SKADE. Første gang jegeren slår PÅ sending etterpå, går de ut som
   * ekte meldinger.
   *
   * Vakta (`utgaaendeNektet`) spørres derfor FØR `kø.push`, og den nekter av to
   * uavhengige grunner: at øvingen pågår, og at kommandoen selv bærer
   * øvingsflagget et sted i strukturen. EMCON-oppførselen kan fortsatt prøves ut —
   * bare med ekte data, som er den eneste øvingen som er verdt noe her.
   */
  function køEllerSend(k: UllKommando): boolean {
    const nektet = utgaaendeNektet({ ovelseAktiv: ovelse, kommando: k });
    if (nektet) {
      kunngjører.si(nektet);
      return false;
    }
    kø.push(k);
    if (!sendingPa) {
      kunngjører.si(`Sending er AV. Handlingen ligger i køen (${kø.length}). Ingenting er strålt.`);
      return false;
    }
    return tømKø();
  }

  /** Send alt i køen. Krever at sending er på, og at det ikke pågår en øving. */
  function tømKø(): boolean {
    // Belte i tillegg til bukseselene: køen skal aldri KUNNE inneholde noe fra en
    // øving (se `køEllerSend`), men tømmingen er den ene handlingen som faktisk
    // stråler, og den skal spørre vakta selv.
    if (ovelse) {
      kunngjører.si(utgaaendeNektet({ ovelseAktiv: true, kommando: null }) ?? '');
      return false;
    }
    if (!sendingPa) {
      kunngjører.si('Køen kan ikke tømmes med sending AV — slå den på først.');
      return false;
    }
    if (kø.length === 0) return true;
    sender = true;
    let alle = true;
    let antall = 0;
    // Stopper ved FØRSTE feil og lar resten stå i køen, i rekkefølge. Å hoppe over
    // en kommando som ikke gikk ut ville sendt bildevurderinger og flagg i en
    // annen rekkefølge enn de ble gjort i.
    while (kø.length > 0) {
      const k = kø[0];
      if (!kilde.send(k)) {
        alle = false;
        break;
      }
      kø.shift();
      antall++;
      sistSendtMs = Date.now();
      // En bildevurdering merkes som SENDT først når den faktisk er ute. Før det
      // står raden som «sett av deg», ikke som kvittert.
      if (k.type === 'vurderBilde' || k.type === 'avvisBilde') {
        const v = bildevurderinger.get(k.utlosningId);
        if (v) bildevurderinger.set(k.utlosningId, { ...v, sendt: true });
      }
    }
    sender = false;
    kunngjører.si(alle
      ? `Køen er tømt — appen strålte ${antall} gang(er).`
      : `Sendte ${antall}. Resten (${kø.length}) ligger fortsatt i køen: broen tok ikke imot.`);
    return alle;
  }

  const ctx: Kontekst = {
    tilstand: () => tilstand,
    naa: () => naaMs,
    tilkobling: () => kilde.tilkobling(),
    okt: () => lesØkt(),
    valgt: () => tilstand.kontakter.find((k) => k.id === valgtId) ?? null,
    velg(id) {
      valgtId = id;
      tegn();
    },
    settFlagg(utlosningId, flagg: Ullflagg) {
      tilstand = settFlagg(tilstand, utlosningId, flagg);
      // Flagget er jegerens EGET, og de andre i laget har bruk for det — men det
      // stråler, så det går gjennom EMCON-porten som alt annet.
      køEllerSend({ type: 'settFlagg', utlosningId, flagg });
      kunngjører.si(`Merket «${ULLFLAGG_NAVN[flagg]}».`);
      tegn();
    },
    send: (k) => køEllerSend(k),
    kunngjør: (m) => kunngjører.si(m),
    påEndring(h) {
      lyttere.add(h);
      return () => lyttere.delete(h);
    },
    tegnPåNytt: () => tegn(),
    ruter: () => ruter,
    sending: sendetilstand,
    settSending(pa) {
      sendingPa = pa;
      kunngjører.si(pa
        ? `SENDING PÅ — appen kan stråle nå. ${kø.length} handling(er) venter i køen.`
        : 'Sending AV — radiotaushet. Utgående handlinger legges i kø.');
      tegn();
    },
    tømKø() {
      const ok = tømKø();
      tegn();
      return ok;
    },
    bildevurdering: (id) => bildevurderinger.get(id),
    vurderBilde(utlosningId, klasse: UgsMalklasse | null) {
      const av = ctx.operatornavn();
      // Vurderingen lagres LOKALT uansett, og merkes med om den er sendt. Et
      // menneske HAR sett bildet — det er sant enten broen har kvittert eller ikke
      // — men raden skal ikke stå som «kvittert» før broen sender utløsningen
      // tilbake med `vurdertAvMenneske: true`.
      bildevurderinger.set(utlosningId, {
        utlosningId,
        klasse,
        avvist: klasse === null,
        av,
        tsMs: Date.now(),
        sendt: false,
      });
      køEllerSend(klasse === null
        ? {
            type: 'avvisBilde',
            utlosningId,
            vurdertAv: av,
            begrunnelse: 'Bildet er SETT, men klassen kunne ikke avgjøres. Det er en reell observasjon av «uavklart».',
          }
        : { type: 'vurderBilde', utlosningId, vurdering: klasse, vurdertAv: av });
      tegn();
    },
    kanViseBilde: () => ULL_MEDIA.length > 0,
    bildeadresse(bildeId) {
      if (ULL_MEDIA.length === 0) return null;
      // `bildeId` er allerede validert av kontrakten mot URL-lignende innhold
      // (ingen `://`, `@` eller `/`), men vi koder den likevel: to lag er riktig
      // når konsekvensen av å ta feil er en URL som peker et annet sted.
      return `${ULL_MEDIA.replace(/\/+$/, '')}/${encodeURIComponent(bildeId)}`;
    },
    velgForMelding(id) {
      valgtMelding = id;
      tegn();
    },
    valgtForMelding: () => valgtMelding,
    // Lagets kallesignal, ikke en persons navn: det er laget som står for
    // meldingen, og et personnavn i en app som kan bli tatt er en opplysning vi
    // ikke trenger å gi bort.
    operatornavn: () => tilstand.post?.kallesignal ?? tilstand.post?.navn ?? 'ULL (ukjent lag)',
  };

  // ── Skjelett ──────────────────────────────────────────────────────────────

  const topp = div('topp');
  const toppLag = span('topp-post');
  const toppTilkobling = span();
  const toppEmcon = span('topp-emcon');
  const toppKlokke = span('topp-klokke');
  topp.append(span('topp-merke', 'ULL'), toppLag);
  const toppHøyre = div('topp-hoyre');
  // Øvingsknappen i toppstripa I TILLEGG til den i tomtilstanden: nodepanelet kan
  // lukkes, og da forsvinner den eneste veien inn i en øving. Den finnes bare når
  // porten tillater det. Er øvingen PÅ, står av-knappen i banneret — to av-knapper
  // ville vært to steder å lete.
  if (!ovelse && ovelsevedtak.nektet === null) {
    toppHøyre.append(knapp('Øvingsdata', () => settOvelse(true), {
      variant: 'stille',
      tittel: 'Fyll flata med et oppdiktet, tydelig merket øvingsbilde. Kan ikke sende noe til SKADE, '
        + 'og ingenting kan legges i EMCON-køen mens det står på.',
    }));
  }
  toppHøyre.append(toppEmcon, toppTilkobling, toppKlokke);
  topp.append(toppHøyre);

  // Sendestripa: full bredde når appen KAN stråle. Den er ikke et varsel om en
  // feil, den er en påminnelse om en tilstand — og den skal ikke kunne overses i
  // et sideblikk.
  const sendestripe = div('sendestripe');
  sendestripe.setAttribute('role', 'status');
  sendestripe.hidden = true;

  /**
   * EMCON-FORKLARINGEN — FUNN D.
   *
   * ══ HVA SOM VAR GALT ═══════════════════════════════════════════════════════
   * Bryteren sa «Radiotaushet. Slå på for å kunne sende til SKADE.» Det forteller
   * HVA knappen gjør, og ingenting om NÅR man bruker den eller hva som skjer med
   * det man har gjort i mellomtiden. Og det sto bare i en `title` — en tooltip
   * finnes ikke for den som betjener flata med hansker på en berøringsskjerm, og
   * ikke for en skjermleser før fokus tilfeldigvis havner der.
   *
   * ══ HVA SOM STÅR HER NÅ ════════════════════════════════════════════════════
   * En permanent, synlig linje som beskriver DET KODEN FAKTISK GJØR — ikke
   * doktrine, som vi ikke skal finne opp:
   *   * med sending AV legges hver utgående handling i køen og blir liggende,
   *   * køen tømmes bare av et bevisst trykk, og det finnes INGEN periodisk
   *     opplasting i appen (`setInterval` i denne fila TEGNER, den sender ikke),
   *   * med sending PÅ går hver handling ut med en gang.
   * Alt dette kan etterprøves i `køEllerSend`/`tømKø` over.
   */
  const emconForklaring = div('emcon-forklaring');

  const flate = div('flate');
  const omrSensorer = div('omr-sensorer');
  const omrSekvens = div('omr-sekvens');
  const omrDekning = div('omr-dekning');
  const omrHøyre = div('omr-hoyre');
  flate.append(omrSensorer, omrSekvens, omrDekning, omrHøyre);

  const paneler = {
    sensorer: lagPanel({ nokkel: 'sensorer', tittel: 'Utlagte noder', kanLosnes: true }),
    // Hovedpanelet kan IKKE lukkes: sekvensanalysen er hele hensikten med appen.
    sekvenser: lagPanel({ nokkel: 'sekvenser', tittel: 'Sekvensanalyse', kanLukkes: false, kanLosnes: true }),
    dekning: lagPanel({ nokkel: 'dekning', tittel: 'Dekning og hull', kanLosnes: true }),
    bilder: lagPanel({ nokkel: 'bilder', tittel: 'Bildekø', kanLosnes: true }),
    melding: lagPanel({ nokkel: 'melding', tittel: 'Melding til SKADE', kanLosnes: true }),
  } as const;

  omrSensorer.append(paneler.sensorer.rot);
  omrSekvens.append(paneler.sekvenser.rot);
  omrDekning.append(paneler.dekning.rot);
  omrHøyre.append(paneler.bilder.rot, paneler.melding.rot);

  // EMCON-kontrollene ligger i sekvenspanelets tittellinje I TILLEGG til
  // toppstripa. To steder er med vilje: toppstripa er der man LESER tilstanden,
  // tittellinja er der man handler. Begge leser samme `sendetilstand()`.
  const emconKnapp = document.createElement('button');
  emconKnapp.type = 'button';
  emconKnapp.className = 'pk pk-stille emcon-knapp';
  emconKnapp.addEventListener('click', () => ctx.settSending(!sendingPa));
  const køKnapp = document.createElement('button');
  køKnapp.type = 'button';
  køKnapp.className = 'pk pk-stille ko-knapp';
  køKnapp.addEventListener('click', () => { ctx.tømKø(); });
  paneler.sekvenser.leggIHode(emconKnapp);
  paneler.sekvenser.leggIHode(køKnapp);

  rot.append(topp, sendestripe, emconForklaring, flate, kunngjører.rot);

  // ── DEN PERMANENTE ØVINGSMERKINGEN (krav 2) ──────────────────────────────
  // To merker, ikke ett: BANNERET øverst sier hva som er galt, RAMMA rundt hele
  // viewporten gjør at også et UTSNITT av skjermen bærer merket — og et utsnitt er
  // nettopp det som klippes inn i en rapport eller vises fram på en telefon.
  // Banneret kan ikke lukkes; knappen slår av øvingen.
  const banner = ovelse
    ? ovelsebanner(
        () => settOvelse(false),
        'ØVINGSDATA — IKKE EKTE DATA. Nodene, utløsningene og aksene under er oppdiktet. '
        + 'Ingenting kan legges i EMCON-køen eller sendes til SKADE mens dette står på.',
      )
    : null;
  settOvelsesramme(ovelse);
  if (banner) rot.insertBefore(banner, sendestripe);

  // Pop-out: ett panel alene i vinduet. Ekte `window.open`, ikke et falskt lag.
  // Monteringen bor i panelmalen (`montérPoppetPanel`) fordi den var ordrett lik i
  // NJORD. `behold` er ikke pynt: uten banneret ville et poppet vindu fullt av
  // øvingsdata stått uten sitt viktigste merke.
  if (montérPoppetPanel(rot, paneler, { behold: banner ? [banner] : [] }) === 'ukjent') return;

  // ── Tegning ───────────────────────────────────────────────────────────────

  function tegnTopp(): void {
    const lag = tilstand.post;
    toppLag.textContent = lag
      ? `${lag.navn}${lag.kallesignal ? ` · ${lag.kallesignal}` : ''}`
      : 'Ingen lag meldt';
    sett(toppTilkobling, tilkoblingsmerke(kilde.tilkobling()));
    // Sending vises med ORD og ikon, ikke med farge alene: flata brukes i mørket.
    // Tre tilstander og ikke to — «på» og «stråler nå» er ikke det samme, og det
    // er i det øyeblikket det STRÅLER at jegeren må se det.
    sett(toppEmcon, sender
      ? statusprikk('STRÅLER NÅ', 'sender')
      : sendingPa
        ? statusprikk(`Sending PÅ${kø.length > 0 ? ` · ${kø.length} i kø` : ''}`, 'sender')
        : statusprikk(`Sending AV${kø.length > 0 ? ` · ${kø.length} i kø` : ''}`, 'stille'));
    toppKlokke.textContent = formaterKlokke(naaMs);

    emconKnapp.textContent = sendingPa ? 'Slå AV sending' : 'Slå PÅ sending';
    // Tittelen sier hva knappen GJØR; forklaringslinja under sier hva tilstanden
    // BETYR. De to skal ikke gjenta hverandre — se `emconForklaring`.
    emconKnapp.title = sendingPa
      ? 'Slår av sendingen. Appen slutter å stråle, og nye handlinger legges i kø.'
      : 'Slår på sendingen. Appen kan stråle, og køen kan tømmes.';
    emconKnapp.disabled = ovelse;
    if (ovelse) {
      emconKnapp.title = 'ØVING PÅGÅR — appen kan ikke stråle. Slå av øvingsdata for å bruke EMCON-bryteren.';
    }
    køKnapp.textContent = `Send køen (${kø.length})`;
    køKnapp.disabled = ovelse || kø.length === 0 || !sendingPa;
    køKnapp.title = ovelse
      ? 'ØVING PÅGÅR — køen er stengt, og ingenting kan sendes til SKADE.'
      : kø.length === 0
        ? 'Ingenting i køen.'
        : sendingPa
          ? `Sender ${kø.length} handling(er) NÅ.`
          : 'Slå på sending først — køen tømmes ikke av seg selv.';

    sett(sendestripe);
    sendestripe.hidden = !sendingPa;
    if (sendingPa) {
      sendestripe.append(ikon('sending'));
      sendestripe.append(span('sendestripe-tekst',
        'SENDING ER PÅ — appen kan stråle. Slå den av når meldingen er ute.'
        + (sistSendtMs !== null ? ` Sist strålt ${formaterKlokke(sistSendtMs)}.` : ' Ingenting er strålt ennå.')));
    }

    tegnEmconForklaring();
  }

  /**
   * Hva EMCON betyr FOR DENNE POSTEN, og hva som skjer med køen. FUNN D.
   *
   * Teksten beskriver bare det koden gjør — ingen oppdiktet doktrine. Den står
   * SYNLIG og permanent, ikke i en tooltip: flata betjenes med hansker i mørket,
   * og en tooltip finnes ikke da. Tallet på hva som ligger i køen er med, fordi
   * «3 i kø» er det som gjør regelen konkret.
   */
  function tegnEmconForklaring(): void {
    const iKø = kø.length === 0
      ? 'Køen er tom.'
      : `${kø.length} handling(er) ligger i køen og er IKKE sendt.`;
    const tekst = ovelse
      ? 'ØVING PÅGÅR. EMCON-bryteren er låst, køen er stengt, og ingenting kan sendes til SKADE — '
        + 'øvingsdata skal aldri kunne bli en melding noen handler på.'
      : sendingPa
        ? `SENDING PÅ betyr at appen kan stråle på lenken inn til SKADE, og at posten kan peiles inn på den. `
          + `Hver handling du gjør går ut med en gang. ${iKø} Slå av igjen når meldingen er ute.`
        : `RADIOTAUSHET (EMCON): appen sender ingenting, og posten stråler ikke på lenken inn til SKADE. `
          + `Alt du gjør — flagg, bildevurderinger og meldinger — legges i kø og blir liggende der. ${iKø} `
          + `Køen går ut først når du slår PÅ sending og trykker «Send køen»; den tømmes aldri av seg selv, `
          + `og appen har ingen periodisk opplasting.`;
    // `role=status` og ikke `alert`: dette er en tilstand som skal kunne leses, ikke
    // et varsel som skal avbryte. Radiotaushet er normaltilstanden.
    emconForklaring.setAttribute('role', 'status');
    emconForklaring.classList.toggle('emcon-forklaring-pa', sendingPa && !ovelse);
    sett(emconForklaring, span('emcon-forklaring-tekst', tekst));
  }

  function tilkoblingsmerke(t: Tilkobling): HTMLElement {
    switch (t.tilstand) {
      case 'tilkoblet':
        return statusprikk('Bro tilkoblet', 'frisk');
      case 'ovelse':
        return merke('ØVINGSDATA', 'ovelse');
      case 'kobler':
        return statusprikk('Kobler …', 'treg');
      case 'ikke-konfigurert':
        return statusprikk('Ingen bro satt opp', 'stille');
      default:
        return statusprikk('Bro nede', 'borte');
    }
  }

  // Hvilke noderader som står med «Vis detaljer» åpen. Flata tegnes om hvert
  // sekund, og uten dette ville utvidelsen smelt igjen ett sekund etter at
  // jegeren åpnet den.
  const åpneNodeDetaljer = new Set<string>();

  /**
   * Sensorstolpen — de utlagte nodene.
   *
   * Hver rad viser TO aldre, og skillet er hele grunnen til at raden ser ut som
   * den gjør: LIVSTEGN (hjertebank) er helsa, SISTE UTLØSNING er aktiviteten. En
   * node som lever og ikke har utløst på en time er helt frisk — det har bare ikke
   * gått noen der. Slo vi dem sammen, ville hver velfungerende node i skogen sett
   * død ut, og da slutter jegeren å lese stolpen.
   *
   * UX-RAPPORTEN (J3): raden åpner nå med ÉN setning i vanlig språk («Node OK —
   * livstegn 4 min, batteri målt OK»), og alt teknisk — de to aldrene, spenning
   * og utladningskurve-forbehold, hopp-telling og broens grunner ORDRETT — står
   * bak «Vis detaljer». Ingenting er fjernet, bare flyttet bak et klikk, og
   * klikket virker med hansker (i motsetning til en tooltip).
   */
  function tegnSensorer(): void {
    if (tilstand.sensorer.length === 0) {
      const t = kilde.tilkobling();
      const hvaKommer =
        'Når ULL-broen melder inn de utlagte nodene — akustisk, seismisk, PIR, kamera, brytertråd og '
        + 'magnetisk — kommer de opp her, med livstegn og siste utløsning HVER FOR SEG. De to betyr ulike '
        + 'ting for en UGS: livstegn er om noden lever, siste utløsning er om noe har gått der.';
      sett(
        paneler.sensorer.kropp,
        t.tilstand === 'ikke-konfigurert'
          ? manglerKilde({
              // FUNN B: forklaringen på norsk FØRST, variabelnavnet dempet til
              // slutt, og et neste steg jegeren kan trykke på nå.
              forklaring: `${t.forklaring} — flata er tom fordi det ikke finnes noen noder å vise`,
              hvaKommer,
              neste: t.neste,
              handling: { tekst: 'Start øvingsdata', gjør: () => settOvelse(true) },
              teknisk: t.variabel,
            })
          : manglerKilde({
              forklaring: 'Ingen noder er meldt inn ennå',
              hvaKommer,
              // Grunnen til at det IKKE står en øvingsknapp her står ordrett, med
              // porten som kilde: en knapp som mangler uten forklaring leses som
              // en feil i appen.
              neste: 'Broen er konfigurert, så dette er en driftssak: sjekk at ULL-broen kjører i skjulstedet.'
                + (ovelsevedtak.nektet ? ` ${ovelsevedtak.nektet}` : ''),
              teknisk: `Forsøkt: ${t.adresse}`,
            }),
      );
      paneler.sensorer.settUnder('0 noder');
      return;
    }

    const liste = div('sensorliste');
    const statuser = sensorStatuser(tilstand, naaMs);

    tilstand.sensorer.forEach((s, i) => {
      const st = statuser[i];
      const rad = div(`sensorrad sensorrad-${nivaaKlasse(st.nivaa)}`);
      rad.append(span('sensorrad-prikk', ''));
      const høyre = div();
      const navnlinje = div('sensorrad-navn');
      navnlinje.append(ikon(artIkon(s.art)));
      navnlinje.append(span('', `${SENSORART_NAVN[s.art]} — ${s.navn}`));
      høyre.append(navnlinje);
      if (s.ovelse) høyre.append(merke('ØVING', 'ovelse'));

      // Én setning i vanlig språk, fra en REN og testet funksjon. Nivåordet er
      // det samme som i detaljene — oppsummeringen og detaljene kan aldri si to
      // ulike ting om samme node.
      høyre.append(span('sensorrad-oppsum', nodeoppsummering({
        nivaa: st.nivaa,
        livstegnAlderMs: st.alderMs,
        batteri: s.ugs
          ? { kilde: s.ugs.batteri.kilde, advarsel: batteriAdvarsel(s.ugs.batteri) !== null }
          : null,
      })));

      const detaljer = document.createElement('details');
      detaljer.className = 'sensorrad-detaljer';
      detaljer.open = åpneNodeDetaljer.has(s.id);
      detaljer.addEventListener('toggle', () => {
        if (detaljer.open) åpneNodeDetaljer.add(s.id);
        else åpneNodeDetaljer.delete(s.id);
      });
      const oppsummering = document.createElement('summary');
      oppsummering.textContent = 'Vis detaljer';
      detaljer.append(oppsummering);
      const kropp = div('sensorrad-detaljekropp');

      kropp.append(span('sensorrad-alder',
        st.alderMs === null
          ? 'Livstegn: ALDRI hørt'
          : `Livstegn: ${FRISKHET_NAVN[st.friskhet]} · ${formaterAlder(st.alderMs)}`));
      const siste = sisteUtlosningFor(tilstand, s.id);
      kropp.append(span('sensorrad-alder',
        siste === null
          ? 'Siste utløsning: ingen — normalt, en UGS er stille til noe passerer'
          : `Siste utløsning: ${formaterAlder(Math.max(0, naaMs - siste))}`));
      if (s.ugs) kropp.append(span('sensorrad-alder', `Batteri: ${batteriTekst(s.ugs.batteri)}`));
      // Grunnene ORDRETT. En oversettelse til vår egen ordliste ville skjult hva
      // som faktisk er galt med noden.
      for (const g of st.grunner) kropp.append(div('sensorrad-grunn', g));

      detaljer.append(kropp);
      høyre.append(detaljer);
      rad.append(høyre);
      liste.append(rad);
    });

    sett(paneler.sensorer.kropp, liste);
    const tillit = postTillit(statuser);
    paneler.sensorer.settUnder(`${tillit.friske} av ${tillit.totalt} OK · ${versteTekst(tillit)}`);
  }

  /** Artsikonet. Tabellen i `ui/ikoner.ts` er kilden; dette er bare oppslaget. */
  function artIkon(art: UgsArt): (typeof ART_IKON)[UgsArt] {
    return ART_IKON[art];
  }

  /** Lagets verste nodetilstand som lesbar tekst. */
  function versteTekst(t: PostTillit): string {
    return NIVAA_NAVN[t.verste];
  }

  function tegn(): void {
    naaMs = Date.now();
    tegnTopp();
    tegnSensorer();

    const sek = tegnSekvensanalyse(paneler.sekvenser.kropp, ctx);
    paneler.sekvenser.settUnder(
      sek.antall === 0
        ? 'ingen sekvenser'
        : `${sek.antall} sekvens(er)`
          + `${sek.akutte > 0 ? ` · ${sek.akutte} pågår` : ''}`
          + `${sek.utenPosisjon > 0 ? ` · ${sek.utenPosisjon} uten posisjon` : ''}`,
    );

    const dek = tegnDekning(paneler.dekning.kropp, ctx);
    paneler.dekning.settUnder(
      dek.ruter === 0
        ? 'ingen akse'
        : dek.storsteHullM === null
          ? `${dek.ruter} akse(r)`
          : `største hull ${Math.round(dek.storsteHullM)} m`,
    );

    const bil = tegnBilde(paneler.bilder.kropp, ctx);
    paneler.bilder.settUnder(bil.ikkeSett > 0
      ? `${bil.ikkeSett} ikke sett`
      : `${bil.kvittert + bil.settAvDeg} vurdert`);

    const mel = tegnMelding(paneler.melding.kropp, ctx);
    paneler.melding.settUnder(mel.harUtkast ? 'utkast under arbeid' : `${mel.sendte} levert i denne økta`);

    for (const l of lyttere) l();
  }

  // ── Abonnementer ──────────────────────────────────────────────────────────

  kilde.påMelding((m) => {
    // Øvingsstempelet ligger på KONVOLUTTEN (`m.ovelse`), satt av øvingskilden.
    // `bruk` leser det selv og bærer det ned på entitetene — det sendes ikke inn
    // som en flate-lokal `demo`-boolean lenger, nettopp for at en app-variabel
    // ikke skal kunne komme i utakt med hva meldingen faktisk er.
    tilstand = bruk(tilstand, m);
    // Ruter er PLANDATA og har ingen plass i `Flatetilstand` — se `brukRute`.
    if (m.type === 'rute') ruter = brukRute(ruter, m.rute, m.ovelse === true);
    // En utløsning som forsvinner skal ikke etterlate et valg som peker i tomma.
    if (valgtId && !tilstand.kontakter.some((k) => k.id === valgtId)) valgtId = null;
    tegn();
  });
  kilde.påTilkobling(() => tegn());

  // Sekundtikk: alderstallene endres uten at det kommer meldinger. MERK at dette
  // er den ENESTE timeren i appen, og den TEGNER — den sender ingenting. Se
  // EMCON-avsnittet i filhodet.
  const tikk = window.setInterval(tegn, 1000);
  window.addEventListener('beforeunload', () => {
    window.clearInterval(tikk);
    kilde.lukk();
  });

  tegn();

  // Escape tømmer valget — samme forventning som i COP og NJORD. Lyttere legges
  // her, inne i `start()`, fordi den trenger `ctx` for å tegne om.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const a = document.activeElement;
    const tag = a?.tagName?.toUpperCase() ?? '';
    // Escape i et skrivefelt skal forlate feltet, ikke rive vekk valget under.
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (valgtId !== null) ctx.velg(null);
  });
}

start();
